import { Button, Input, Space, Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useEffect } from "react";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import ManageAdjustmentModal, {
  rules,
} from "../../../../components/Settings/Leave/ManageAdjustmentModal";
import ViewAdjustmentModal from "../../../../components/Settings/Leave/ViewAdjustmentModal";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import CustomTable from "../../../../components/ui/CustomTable";
import { dateFormat, dbDateFormat } from "../../../../constants/constants";
import {
  useGetLeaveAdjustmentListQuery,
  useUpdateAdjustmentStatusMutation,
} from "../../../../features/leaveType/leaveTypeApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { GetLeaveAdjustmentParamsType } from "../../../../types";
import { countParPageSerialNumber } from "../../../../utils/number";
import { capitalize } from "../../../../utils/string";
import { addKeyInArray } from "../../../../utils/utils";
import { leaveSettingsTabList } from "./LeaveType";

type StatusType = {
  id: number;
  body: {
    is_active: boolean;
  };
};

export default function Adjustment() {
  const { open, handleOpen, handleClose } = useModal<
    StatusType | number,
    "STATUS"
  >();
  const {
    page,
    limit,
    keyword,
    handlePagination,
    handleLimit,
    handleSearch,
    filter,
  } = useTable<GetLeaveAdjustmentParamsType>();
  const { data, isLoading, isError, error } = useGetLeaveAdjustmentListQuery({
    params: { ...filter, search: keyword },
  });

  const [
    updateAdjustmentStatus,
    {
      isSuccess: isUpdateSuccess,
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateAdjustmentStatusMutation();
  useError(isError, error, "Failed fetching adjustment");
  useError(
    isUpdateError,
    updateError,
    `Adjustment ${
      typeof open.data !== "number" && open?.data?.body?.is_active
        ? "active"
        : "inactive"
    } fail`
  );

  const columns: ColumnsType<any> = [
    {
      title: "#SL",
      dataIndex: "index",
      align: "center",
      width: 50,
      render: (_: any, __: any, index: number) =>
        countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Rule Name",
      dataIndex: "rule_name",
      render: (val) => rules?.find((item) => item?.value === val)?.label,
    },
    {
      title: "Applied to",
      dataIndex: "applicable_for",
      render: (val) => capitalize(val),
    },
    {
      title: "Effective From",
      dataIndex: "effective_from",
      render: (val) => moment(val, dbDateFormat).format(dateFormat),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (val, record) => (
        <Space size={8}>
          <Switch
            className="custom-switch"
            checked={val}
            disabled={isUpdateLoading}
            onChange={(checked) => {
              handleOpen({
                type: "STATUS",
                data: {
                  id: record?.id,
                  body: {
                    is_active: checked ? true : false,
                  },
                },
              });
            }}
          />
          <span className="font-medium text-header">
            {val ? "Activated" : "Inactive"}
          </span>
        </Space>
      ),
    },
    {
      title: "Action",
      align: "center",
      render: (val) => (
        <Space size={8}>
          <Button
            className="action-button"
            size="small"
            icon={<ViewIcon />}
            onClick={() =>
              handleOpen({
                type: "VIEW",
                data: val?.id,
              })
            }
          />
          <Button
            icon={<EditIcon />}
            size="small"
            className="action-button"
            onClick={() =>
              handleOpen({
                type: "UPDATE",
                data: val?.id,
              })
            }
          />
        </Space>
      ),
    },
  ];

  const handleStatus = () => {
    if (typeof open.data !== "number" && open?.data?.id) {
      updateAdjustmentStatus({ id: open.data?.id, data: open.data?.body });
    }
  };

  useEffect(() => {
    if (isUpdateSuccess) {
      toast(
        "success",
        `Adjustment successfully ${
          typeof open.data !== "number" && open?.data?.body?.is_active
            ? "activated"
            : "inactive"
        }`
      );
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateSuccess]);

  return (
    <SidebarLayout title="Adjustment" tabList={leaveSettingsTabList}>
      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.total_count}
        dataSource={addKeyInArray(data?.data?.leave_adjustment_policies)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              onChange={handleSearch}
            />

            <Button
              className="middle-button"
              type="primary"
              icon={<AddIcon />}
              onClick={() =>
                handleOpen({
                  type: "ADD",
                })
              }
            >
              Add New
            </Button>
          </>
        }
      />

      {(open.type === "ADD" || open.type === "UPDATE") &&
        (typeof open?.data === "number" ||
          typeof open?.data === "undefined") && (
          <ManageAdjustmentModal
            open={open.open}
            type={open.type}
            data={open.data}
            handleClose={handleClose}
          />
        )}
      {open.type === "VIEW" &&
        (typeof open?.data === "number" ||
          typeof open?.data === "undefined") && (
          <ViewAdjustmentModal
            open={open.open}
            data={open.data}
            handleClose={handleClose}
          />
        )}

      {open?.type === "STATUS" && (
        <ConfirmModal
          open={open.open}
          handleClose={handleClose}
          handleOk={handleStatus}
          confirmTitle="Wait!"
          questionText={`Are you sure you want to ${
            typeof open.data !== "number" && open?.data?.body?.is_active
              ? "active"
              : "inactive"
          } this adjustment?`}
          okText="Yes"
          cancelText="No"
          okLoading={isUpdateLoading}
        />
      )}
    </SidebarLayout>
  );
}
